import _ from 'lodash';
import type { Edge, Node } from 'reactflow';

import { flowDataToModules } from './graph-data-to-modules';

import {
    CHANNEL_MCHANNELS_BOT_ID_MAPPING,
    DEFAULT_CHANNEL,
    DEFAULT_MCHANNELS_BOT_ID,
} from '@/data/channels';
import { Bot, BotModuleData, NewBot, TaskValue } from '@/domain/bot';
import {
    BotMappieDialog,
    BotMappieNewVersion,
    BotMappieUiConfig,
} from '@/domain/bot-mappie';
import { OmnichannelConfigTTSMappie } from '@/domain/omnichannel-config';

/**
 * Transforms bot from format used in browser to format used in database (mappie)
 * @param bot
 * @param account - account user owning the bot
 * @returns botMappieNewVersion - bot specification in format used in mappie
 */

export const transformBotToMappieVersion = (
    bot: Bot | NewBot,
    nodeHandles?: Record<string, string[]> | undefined,
) => {
    let dialog: BotMappieDialog;
    let uiConfig: BotMappieUiConfig | undefined;

    let taskValues: Record<string, string> | undefined;

    if ((bot as any).nodesReactFlow && (bot as any).edgesReactFlow) {
        const currentBotVersion = bot as Bot;

        const modulesObject = flowDataToModules(
            //fix types below
            // @ts-ignore
            prepareNodesForMappie(bot.nodesReactFlow),
            currentBotVersion.edgesReactFlow,
            nodeHandles,
        );

        const edgeToRootModule = currentBotVersion.edgesReactFlow.find(
            (edge: Edge) => edge.source === 'start',
        );

        dialog = {
            modules: {
                ...modulesObject,
            },
            root_module: edgeToRootModule
                ? edgeToRootModule.target // when edge starts at starts then the node its going to is root_module
                : null,
            stickers: bot.stickers,
            prompt_texts: currentBotVersion.prompt_texts,
            intent_texts: currentBotVersion.intent_texts,
            entity_texts: currentBotVersion.entity_texts,
        };

        uiConfig = {
            module_positions: _.fromPairs(
                currentBotVersion.nodesReactFlow.map((node) => [
                    node.id,
                    { x: node.position.x, y: node.position.y },
                ]),
            ),
        };

        taskValues = taskValuesToObject(currentBotVersion.taskValues);
    } else {
        dialog = {
            modules: {
                // end module is created as default module and not editable afterwards
                end: {
                    valid: true,
                    type: 'Exit',
                },
            },
            root_module: 'end',
            //TODO  - temporary solution
            // @ts-ignore
            name: bot.name,
            language: bot.language,
        };
    }

    const botMappieNewVersion = {
        name: bot.name,
        description: bot.description,
        language: bot.language,
        channel: bot.channel,
        mchannels_bot_id: getMchannelsBotId(bot),
        omnichannel_config: {
            voice:
                bot.channel === 'voice'
                    ? {
                          initial_user_response_timeout: Number(
                              bot.voiceConfig?.initialUserResponseTimeout,
                          ),
                          inactivity_timeout: Number(
                              bot.voiceConfig?.inactivityTimeout,
                          ),
                          tts: {
                              ...bot.voiceConfig?.ttsVoice,
                              prosody: bot.voiceConfig?.ttsProsody,
                          } as OmnichannelConfigTTSMappie,
                          stt: {
                              language: bot.voiceConfig?.ttsVoice?.language,
                              provider: bot.voiceConfig?.stt?.provider,
                              end_of_speech_timeout:
                                  bot.voiceConfig?.stt?.endOfSpeechTimeout ??
                                  500,
                          },
                      }
                    : undefined,
            sms:
                bot.channel === 'sms'
                    ? { sender: 'TELMA AI', provider: 'alaris' }
                    : undefined,
        },
        dialog: dialog,
        ui_config: uiConfig,
        task_values: taskValues,
        permanent: bot.permanent,
    };

    if (botMappieNewVersion?.omnichannel_config?.voice?.tts?.prosody?.rate) {
        botMappieNewVersion.omnichannel_config.voice.tts.prosody = {
            ...botMappieNewVersion.omnichannel_config.voice.tts.prosody,
            rate: `${String(
                botMappieNewVersion.omnichannel_config.voice.tts.prosody.rate,
            )}%`,
        };
    }
    //TODO! this is a temporary solution
    // @ts-ignore
    botMappieNewVersion.dialog.initial_user_response_timeout =
        bot.channel === 'voice'
            ? Number(bot.voiceConfig?.initialUserResponseTimeout)
            : undefined;

    return botMappieNewVersion as BotMappieNewVersion;
};

/**
 * Prepares node data for Mappie format by:
 * - Converting string alternatives to arrays (YesNoConfirmation)
 * - Formatting phone numbers (SMSModule)
 * - Parsing JSON strings to objects (CustomInternalModule, RestCallout)
 */
export const prepareNodesForMappie = (nodes: Node<BotModuleData>[]) => {
    const transformedNodes = nodes.map((node) => {
        switch (node?.data?.type) {
            case 'YesNoConfirmation':
                return {
                    ...node,
                    data: {
                        ...node.data,
                        params: {
                            ...node.data.params,
                            yes_alternatives:
                                typeof node.data.params.yes_alternatives ===
                                'string'
                                    ? getAraryOfAlternativesFromString(
                                          node.data.params.yes_alternatives,
                                      )
                                    : node.data.params.yes_alternatives,
                            no_alternatives:
                                typeof node.data.params.no_alternatives ===
                                'string'
                                    ? getAraryOfAlternativesFromString(
                                          node.data.params.no_alternatives,
                                      )
                                    : node.data.params.no_alternatives,
                            escape_alternatives:
                                typeof node.data.params.escape_alternatives ===
                                'string'
                                    ? getAraryOfAlternativesFromString(
                                          node.data.params.escape_alternatives,
                                      )
                                    : node.data.params.escape_alternatives,
                        },
                    },
                };
            case 'SMSModule':
                return {
                    ...node,
                    data: {
                        ...node.data,
                        params: {
                            ...node.data.params,

                            isCustomPhone: undefined,
                            phonePrefix: undefined,
                            phoneNumber: undefined,
                            target_sms_number: node.data.params.isCustomPhone
                                ? `${node.data.params.phonePrefix}${node.data.params.phoneNumber}`
                                : undefined,
                        },
                    },
                };
            case 'CustomInternalModule':
                return {
                    ...node,
                    data: {
                        ...node.data,
                        params: parseJsonStringToObject(node.data.params),
                    },
                };
            case 'RestCallout':
                return {
                    ...node,
                    data: {
                        ...node.data,
                        params: {
                            ...node.data.params,
                            request_template: parseJsonStringToObject(
                                node.data.params.request_template,
                            ),
                        },
                    },
                };
            default:
                return node;
        }
    });

    return transformedNodes;
};
const getAraryOfAlternativesFromString = (string: string) => {
    const array = string
        .split(',')
        .map((phrase: string) => phrase.trim())
        .filter((phrase: string) => phrase.length > 0);

    return array;
};

export const taskValuesToObject = (taskValuesArray: TaskValue[]) => {
    const taskValues: { [key: string]: string } = taskValuesArray
        .filter((row) => row.variableName !== '')
        .reduce(
            (content, row) => ({ ...content, [row.variableName]: row.value }),
            {},
        );
    return taskValues;
};

const getMchannelsBotId = (bot: Bot | NewBot): string => {
    if (bot.mchannelsBotId) {
        return bot.mchannelsBotId;
    }

    const isProd = process.env.deployment_namespace === 'mama-public';
    const channel = bot.channel ?? DEFAULT_CHANNEL;

    let mChannelsBotId =
        CHANNEL_MCHANNELS_BOT_ID_MAPPING[channel] ?? DEFAULT_MCHANNELS_BOT_ID;

    if (!isProd) {
        mChannelsBotId += '-dev';
    }
    return mChannelsBotId;
};

const parseJsonStringToObject = (value: any): any => {
    if (typeof value === 'object' && value !== null) {
        return value;
    }
    if (typeof value === 'string') {
        try {
            return JSON.parse(value);
        } catch (error) {
            console.warn('Failed to parse JSON string:', error);
            return value;
        }
    }
    return value;
};
